import React, { useState, useCallback, useMemo } from 'react'
import { motion } from 'framer-motion'
import { 
  Activity, 
  Database, 
  Clock, 
  CheckCircle, 
  FileText,
  Play,
  ChevronRight,
  TrendingUp,
  Users,
  Timer
} from 'lucide-react'
import ReactJsonView from 'react-json-view'

import { DSAPMockServer, Scenario, DSAPRequest, DSAPResponse } from '../mock/dsap-mock'

interface Props {
  onComplete: (data: any) => void
  selectedScenario: Scenario | null
  accessDecision: any
  mockServer: DSAPMockServer
  addToHistory: (request: DSAPRequest, response: DSAPResponse) => void
}

export default function Step3LogActivity({
  onComplete,
  selectedScenario,
  accessDecision,
  mockServer,
  addToHistory
}: Props) {
  const [dataFetched, setDataFetched] = useState(false)
  const [dataOperation, setDataOperation] = useState<any>(null)
  const [auditLogged, setAuditLogged] = useState(false)
  const [auditData, setAuditData] = useState<any>(null)
  const [showDataJson, setShowDataJson] = useState(false)
  const [showAuditJson, setShowAuditJson] = useState(false)
  const [loading, setLoading] = useState(false)

  // Simulate the data operation results
  const simulatedData = useMemo(() => {
    if (!selectedScenario || !accessDecision) return null
    
    const allowedFields = accessDecision.restrictions?.allowedFields || []
    const maxRecords = accessDecision.restrictions?.maxRecords || 0
    const recordCount = Math.min(Math.floor(Math.random() * 50) + 10, maxRecords)
    
    // Generate realistic mock data based on allowed fields
    const mockRecords = Array.from({ length: recordCount }, (_, i) => {
      const record: any = { id: `record_${i + 1}` }
      
      allowedFields.forEach((field: string) => {
        switch (field) {
          case 'name':
            record.name = `User ${i + 1}`
            break
          case 'email':
            record.email = `user${i + 1}@acmecorp.com`
            break
          case 'department':
            record.department = ['Engineering', 'Sales', 'Marketing', 'HR'][i % 4]
            break
          case 'company':
            record.company = `Company ${String.fromCharCode(65 + (i % 26))}`
            break
          case 'phone':
            record.phone = `+1-555-${String(Math.random()).substring(2, 6)}`
            break
          case 'address':
            record.address = `${123 + i} Main St, City ${i + 1}`
            break
          case 'manager':
            record.manager = `Manager ${i % 5 + 1}`
            break
          case 'start_date':
            record.start_date = `202${i % 4}-0${(i % 9) + 1}-15`
            break
          case 'employee_id':
            record.employee_id = `EMP${String(i + 1000)}`
            break
          case 'salary':
            record.salary = Math.floor(Math.random() * 100000) + 50000
            break
          case 'bonus':
            record.bonus = Math.floor(Math.random() * 20000)
            break
          default:
            record[field] = `${field}_value_${i + 1}`
        }
      })
      
      return record
    })
    
    return {
      recordCount,
      records: mockRecords.slice(0, 3), // Show only first 3 for display
      totalRecords: recordCount,
      fields: allowedFields,
      dataTypes: selectedScenario.context.sensitivity,
      operationTime: Math.random() * 200 + 50 // 50-250ms
    }
  }, [selectedScenario, accessDecision])

  const handleFetchData = useCallback(async () => {
    setLoading(true)
    
    // Simulate data fetching delay
    await new Promise(resolve => setTimeout(resolve, Math.random() * 1000 + 500))
    
    setDataOperation(simulatedData)
    setDataFetched(true)
    setLoading(false)
    
    // Auto-show data after a moment
    setTimeout(() => setShowDataJson(true), 500)
  }, [simulatedData])

  const logActivityPayload = useMemo(() => {
    if (!selectedScenario || !accessDecision || !dataOperation) return null
    
    return {
      jsonrpc: '2.0',
      method: 'dsap.logActivity',
      params: {
        agentId: selectedScenario.agentId,
        userId: selectedScenario.userId,
        timestamp: new Date().toISOString(),
        auditRecordId: accessDecision.auditRecordId,
        activity: {
          type: 'data_access',
          resource: selectedScenario.dataResource,
          operation: selectedScenario.operation,
          recordCount: dataOperation.recordCount,
          dataClassifications: selectedScenario.context.sensitivity
        },
        outcome: {
          status: 'success',
          recordsProcessed: dataOperation.recordCount,
          duration: dataOperation.operationTime / 1000 // Convert to seconds
        }
      },
      id: Date.now()
    }
  }, [selectedScenario, accessDecision, dataOperation])

  const handleLogActivity = useCallback(async () => {
    if (!logActivityPayload) return
    
    setLoading(true)
    
    try {
      const response = await mockServer.processRequest(logActivityPayload)
      addToHistory(logActivityPayload, response)
      
      if (response.result) {
        setAuditData(response.result)
        setAuditLogged(true)
        // Auto-show audit response
        setTimeout(() => setShowAuditJson(true), 500)
      }
    } catch (error) {
      console.error('Activity logging failed:', error)
    } finally {
      setLoading(false)
    }
  }, [logActivityPayload, mockServer, addToHistory])

  const handleContinue = useCallback(() => {
    onComplete({ auditData, dataOperation })
  }, [onComplete, auditData, dataOperation])

  return (
    <div className="space-y-6">
      {/* Step Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-dsap-100 rounded-lg flex items-center justify-center">
              <Activity className="h-6 w-6 text-dsap-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Step 3: Simulate Data Operation & Log Activity
            </h3>
            <p className="text-gray-600 mb-4">
              Now that access has been authorized, we'll simulate the actual data operation 
              (fetching records from the database) and then log this activity in the DSAP 
              audit trail for compliance tracking.
            </p>
            
            {accessDecision && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-green-800 text-sm">
                  <CheckCircle className="h-4 w-4" />
                  <span className="font-medium">Access Previously Authorized:</span>
                  <span>{accessDecision.restrictions?.allowedFields?.length || 0} fields</span>
                  <span>•</span>
                  <span>Max {accessDecision.restrictions?.maxRecords || 0} records</span>
                  <span>•</span>
                  <span>Risk: {accessDecision.riskScore}/100</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Data Operation Simulation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">
          Data Operation Simulation
        </h4>
        
        {!dataFetched ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Database className="h-8 w-8 text-blue-600" />
            </div>
            <h5 className="text-lg font-medium text-gray-900 mb-2">
              Ready to Fetch Data
            </h5>
            <p className="text-gray-600 mb-6">
              Simulate accessing the authorized data within the approved restrictions.
            </p>
            <button
              onClick={handleFetchData}
              disabled={loading}
              className={`inline-flex items-center px-6 py-3 text-base font-medium rounded-lg transition-all ${
                loading
                  ? 'bg-blue-400 text-white cursor-wait'
                  : 'bg-blue-600 text-white hover:bg-blue-700 transform hover:scale-105'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Fetching Data...
                </>
              ) : (
                <>
                  <Play className="h-5 w-5 mr-2" />
                  Simulate Data Fetch
                </>
              )}
            </button>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            {/* Operation Results */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h5 className="font-semibold text-green-900">
                    Data Operation Successful
                  </h5>
                  <p className="text-sm text-green-700">
                    {dataOperation?.recordCount} records retrieved in {dataOperation?.operationTime?.toFixed(0)}ms
                  </p>
                </div>
              </div>
              
              {/* Operation Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-3 text-center">
                  <Users className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                  <div className="text-lg font-semibold text-gray-900">
                    {dataOperation?.recordCount}
                  </div>
                  <div className="text-xs text-gray-600">Records</div>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <FileText className="h-5 w-5 text-green-600 mx-auto mb-1" />
                  <div className="text-lg font-semibold text-gray-900">
                    {dataOperation?.fields?.length}
                  </div>
                  <div className="text-xs text-gray-600">Fields</div>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <Timer className="h-5 w-5 text-purple-600 mx-auto mb-1" />
                  <div className="text-lg font-semibold text-gray-900">
                    {dataOperation?.operationTime?.toFixed(0)}ms
                  </div>
                  <div className="text-xs text-gray-600">Duration</div>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <TrendingUp className="h-5 w-5 text-orange-600 mx-auto mb-1" />
                  <div className="text-lg font-semibold text-gray-900">
                    {dataOperation?.dataTypes?.length}
                  </div>
                  <div className="text-xs text-gray-600">Data Types</div>
                </div>
              </div>
            </div>

            {/* Sample Data Preview */}
            <div>
              <h5 className="font-medium text-gray-900 mb-3">Sample Retrieved Data</h5>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="space-y-2">
                  {dataOperation?.records?.slice(0, 3).map((record: any, index: number) => (
                    <div key={index} className="bg-white rounded p-3 text-sm">
                      <div className="flex flex-wrap gap-4">
                        {Object.entries(record).map(([key, value]) => (
                          <div key={key} className="flex-shrink-0">
                            <span className="text-gray-600 font-medium">{key}:</span>
                            <span className="ml-1 text-gray-900">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {dataOperation?.totalRecords > 3 && (
                  <div className="mt-2 text-center text-sm text-gray-500">
                    ... and {dataOperation.totalRecords - 3} more records
                  </div>
                )}
              </div>
            </div>

            {/* Data JSON Toggle */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowDataJson(!showDataJson)}
                className="text-dsap-600 hover:text-dsap-700 font-medium text-sm"
              >
                {showDataJson ? 'Hide' : 'Show'} Data Operation Details
              </button>
              
              {showDataJson && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4"
                >
                  <ReactJsonView
                    src={{
                      operation: 'data_fetch',
                      resource: selectedScenario?.dataResource,
                      recordsRetrieved: dataOperation?.recordCount,
                      allowedFields: dataOperation?.fields,
                      sampleData: dataOperation?.records?.slice(0, 2)
                    }}
                    theme="monokai"
                    collapsed={1}
                    displayDataTypes={false}
                    displayObjectSize={false}
                    enableClipboard={true}
                    name="dataOperation"
                  />
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {/* Audit Logging */}
      {dataFetched && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Audit Trail Logging
          </h4>
          
          {!auditLogged ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-purple-600" />
              </div>
              <h5 className="text-lg font-medium text-gray-900 mb-2">
                Log Data Access Activity
              </h5>
              <p className="text-gray-600 mb-6">
                Record this data operation in the DSAP audit trail for compliance tracking.
              </p>
              <button
                onClick={handleLogActivity}
                disabled={loading}
                className={`inline-flex items-center px-6 py-3 text-base font-medium rounded-lg transition-all ${
                  loading
                    ? 'bg-purple-400 text-white cursor-wait'
                    : 'bg-purple-600 text-white hover:bg-purple-700 transform hover:scale-105'
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    Logging Activity...
                  </>
                ) : (
                  <>
                    <FileText className="h-5 w-5 mr-2" />
                    Log Activity
                  </>
                )}
              </button>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              {/* Audit Success */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-purple-900">
                      Activity Successfully Logged
                    </h5>
                    <p className="text-sm text-purple-700">
                      Audit record ID: {auditData?.logEntryId}
                    </p>
                  </div>
                </div>
                
                {/* Compliance Impact */}
                {auditData?.complianceImpact && (
                  <div className="bg-white rounded-lg p-4">
                    <h6 className="font-medium text-purple-900 mb-3">
                      Compliance Impact Analysis
                    </h6>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-purple-600 font-medium">Affected Regulations:</span>
                        <div className="mt-1">
                          {auditData.complianceImpact.regulationsAffected?.map((reg: string) => (
                            <span
                              key={reg}
                              className="inline-block bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs mr-1 mb-1"
                            >
                              {reg}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <span className="text-purple-600 font-medium">Retention Required:</span>
                        <div className="mt-1 text-purple-800">
                          {auditData.complianceImpact.retentionRequired}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Audit JSON Toggle */}
              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowAuditJson(!showAuditJson)}
                  className="text-dsap-600 hover:text-dsap-700 font-medium text-sm"
                >
                  {showAuditJson ? 'Hide' : 'Show'} Audit Log Response
                </button>
                
                {showAuditJson && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4"
                  >
                    <ReactJsonView
                      src={auditData}
                      theme="monokai"
                      collapsed={1}
                      displayDataTypes={false}
                      displayObjectSize={false}
                      enableClipboard={true}
                      name="auditResponse"
                    />
                  </motion.div>
                )}
              </div>

              {/* Continue Button */}
              <div className="flex justify-end pt-4">
                <button
                  onClick={handleContinue}
                  className="inline-flex items-center px-6 py-3 bg-dsap-600 text-white font-medium rounded-lg hover:bg-dsap-700 transition-colors"
                >
                  Continue to Compliance Report
                  <ChevronRight className="h-4 w-4 ml-2" />
                </button>
              </div>
            </motion.div>
          )}
        </div>
      )}
    </div>
  )
}